import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DataList from '../DataList';
import LeaveRequestPrint from './LeaveRequestPrint';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/Leave.css';

const TYPE_LABELS = {
  annual: 'Annual leave',
  sick: 'Sick leave',
  maternity: 'Maternity leave',
  paternity: 'Paternity leave',
};

const STATUS_LABELS = {
  submitted: 'Waiting for manager',
  manager_approved: 'Waiting for HR',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const apiBase = () => `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/leave`;

const addInclusiveCalendarDays = (startValue, days) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(startValue || '').slice(0, 10));
  const count = Number(days);
  if (!match || !Number.isFinite(count) || count < 1) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + count - 1);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LeaveRequestList = ({ employee }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('/leave-requests', 'Edit');
  const canManager = hasPermission('/leave-requests/manager', 'Edit');
  const canHr = hasPermission('/leave-requests/hr', 'Edit');
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [printRequest, setPrintRequest] = useState(null);
  const [annualLeaveDays, setAnnualLeaveDays] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${apiBase()}/request-defaults`)
      .then((response) => {
        const days = Number(response.data?.annual_leave_days);
        if (!cancelled && Number.isFinite(days) && days > 0) setAnnualLeaveDays(days);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const act = async (row, action, body) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await axios.post(`${apiBase()}/requests/${row.request_id}/${action}`, body || {});
      setMessage('Leave request updated.');
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this request.');
    } finally {
      setBusy(false);
    }
  };

  const employeeField = employee
    ? {
        name: 'employee_id',
        label: 'Employee',
        type: 'dropdown',
        required: true,
        disabled: true,
        tab: 'Main',
        defaultValue: employee.employee_id,
      }
    : {
        name: 'employee_id',
        label: 'Employee',
        type: 'dropdown',
        required: true,
        tab: 'Main',
      };

  const apiConfig = {
    title: 'Leave requests',
    path: '/leave-requests',
    fetchEndpoint: employee
      ? `/hospital-portal/api/employees/leave/requests?employee_id=${encodeURIComponent(employee.employee_id)}`
      : '/hospital-portal/api/employees/leave/requests',
    createEndpoint: '/hospital-portal/api/employees/leave/requests',
    updateEndpoint: '/hospital-portal/api/employees/leave/requests',
    useLocalCaching: false,
    allowAdd: true,
    allowEdit: true,
    allowDelete: false,
    allowExport: false,
    addButtonLabel: 'New request',
    formTitle: 'Leave request',
    gcBucketName: 'sh-employees',
    defaultSortFieldId: 'start_date',
    defaultSortAsc: false,
    dropdownFields: employee
      ? []
      : [{
          name: 'employee_id',
          endpoint: '/hospital-portal/api/employees/leave/workflow-employees',
          valueField: 'employee_id',
          labelField: 'employee_name',
        }],
    staticDropdownFields: [
      ...(employee
        ? [{
            name: 'employee_id',
            options: [{ id: employee.employee_id, name: employee.employee_name }],
          }]
        : []),
      {
        name: 'leave_type',
        options: Object.entries(TYPE_LABELS).map(([id, name]) => ({ id, name })),
      },
    ],
    tabs: [
      { name: 'Main', roles: 'all', default: true },
    ],
    fields: [
      { name: 'request_id', label: 'Request', type: 'text', identifier: true, disabled: true, visible: false, tab: 'Main' },
      employeeField,
      { name: 'leave_type', label: 'Type', type: 'dropdown', required: true, defaultValue: 'annual', tab: 'Main' },
      {
        name: 'start_date',
        label: 'Start date',
        type: 'date',
        required: true,
        tab: 'Main',
        onChange: (value, updatedFormData, setFormData) => {
          const leaveType = updatedFormData.leave_type || 'annual';
          if (leaveType !== 'annual' || !annualLeaveDays || !value) return;
          const endDate = addInclusiveCalendarDays(value, annualLeaveDays);
          if (!endDate) return;
          setFormData({ ...updatedFormData, end_date: endDate });
        },
      },
      { name: 'end_date', label: 'End date', type: 'date', required: true, tab: 'Main' },
      { name: 'certificate_note', label: 'Note', type: 'textarea', maxRows: 4, tab: 'Main' },
      {
        name: 'prevented',
        label: 'Forfeited Leave Allowance',
        type: 'checkbox',
        defaultValue: false,
        className: 'forfeited-allowance-flag',
        visibleWhen: { field: 'leave_type', equals: 'annual' },
        tab: 'Main',
      },
      {
        name: 'signed_documents',
        label: 'Signed Documents',
        type: 'file',
        tab: 'Main',
        accept: 'application/pdf,image/*',
      },
    ],
    additionalValidation: async (formData) => {
      try {
        const response = await axios.post(`${apiBase()}/requests/preview`, {
          request_id: formData.request_id,
          employee_id: formData.employee_id,
          leave_type: formData.leave_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          certificate_note: formData.certificate_note,
        });
        if (!response.data?.ok) {
          return { isValid: false, message: response.data?.errors?.[0] || 'This request cannot be saved.' };
        }
        return { isValid: true };
      } catch (err) {
        return {
          isValid: false,
          message: err.response?.data?.error || 'Could not check this request.',
        };
      }
    },
  };

  const columns = [
    ...(!employee ? [{
      name: 'Employee',
      selector: (row) => row.Employee?.employee_name || '',
      sortable: true,
      filterable: true,
      filterSelector: 'Employee.employee_name',
      type: 'link',
      label: (row) => row.Employee?.employee_name || 'Details',
      path: '/employee-card',
      getState: (row) => ({
        employee: {
          employee_id: row.employee_id,
          employee_name: row.Employee?.employee_name,
        },
      }),
    }] : []),
    { name: 'ID', selector: (row) => row.request_id, identifier: true, omit: true },
    {
      name: 'Type',
      selector: (row) => TYPE_LABELS[row.leave_type] || row.leave_type,
      sortable: true,
    },
    {
      id: 'start_date',
      name: 'Start date',
      selector: (row) => row.start_date,
      sortable: true,
      type: 'date',
    },
    {
      name: 'End date',
      selector: (row) => row.end_date,
      sortable: true,
      type: 'date',
    },
    { name: 'Days', selector: (row) => row.working_days, sortable: true },
    { name: 'Unpaid days', selector: (row) => row.unpaid_days, sortable: true },
    {
      name: 'Forfeited',
      selector: (row) => (row.prevented ? 'Yes' : 'No'),
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => STATUS_LABELS[row.status] || row.status,
      sortable: true,
    },
    {
      name: 'Next step',
      grow: 2,
      minWidth: '220px',
      ignoreRowClick: true,
      allowOverflow: true,
      cell: (row) => (
        <div className="leave-row-actions">
          <button
            type="button"
            className="leave-print-link"
            onClick={(event) => {
              event.stopPropagation();
              setPrintRequest(row);
            }}
          >
            Print
          </button>
          {canManager && row.status === 'submitted' && (
            <button type="button" disabled={busy} onClick={(event) => {
              event.stopPropagation();
              act(row, 'manager-approve');
            }}>
              Manager approve
            </button>
          )}
          {canHr && row.status === 'manager_approved' && (
            <button type="button" disabled={busy} onClick={(event) => {
              event.stopPropagation();
              act(row, 'hr-approve');
            }}>
              HR approve
            </button>
          )}
          {canEdit && ['submitted', 'manager_approved'].includes(row.status) && (
            <button type="button" className="reject" disabled={busy} onClick={(event) => {
              event.stopPropagation();
              act(row, 'reject');
            }}>
              Reject
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {error && <p className="leave-error">{error}</p>}
      {message && <p className="leave-ok">{message}</p>}
      <DataList key={refreshKey} apiConfig={apiConfig} columns={columns} />
      {printRequest && (
        <LeaveRequestPrint request={printRequest} onClose={() => setPrintRequest(null)} />
      )}
    </div>
  );
};

export default LeaveRequestList;
